import { formatISO, parseISO } from "date-fns";
import { Address } from "@origin-digital/address-lookup";
import { useLpgAccountDetails } from "../../lpg/queries";
import { LpgProfileAccountDetails_viewer as Viewer } from "../../lpg/graphql-types/LpgProfileAccountDetails";
import {
  FIELD_UNSUPPORTED,
  FIELD_UPDATE_HOOK_UNSUPPORTED,
  FieldType,
  ProfileFieldMap,
  ProfileFieldMapResult,
} from "../../profileFieldMap";
import {
  useUpdateDob,
  useUpdateEmail,
  useUpdateHomePhone,
  useUpdateLpgAccountProfileContactPreference,
  useUpdateMobile,
  useUpdatePostalAddress,
} from "../../lpg/mutations";
import { ContactPreferenceProp } from "../../../screens/UpdateContactPrefsScreen/ContactOption";
import { DeliveryType } from "../../lpg/graphql-types/globalTypes";
import {
  isDobSet,
  transformPhoneFromInternational,
} from "../../../utils/helpers";
import {
  mapAddressToAustralianAddressInput,
  mapBillingDetailsToContactPreferenceProp,
  mapToLpgContactPreferenceProps,
} from "./lpgDataConverter";

const mapPlugin = (
  viewer: Viewer | null | undefined
): ProfileFieldMap | undefined => {
  if (!viewer) {
    return undefined; // is loading or in error
  }

  const account = viewer.accounts?.[0];

  if (!account) {
    throw new Error("No accounts found for LPG only user");
  }

  const contact = account.contact;
  const billing = account.billing;
  const contactPreferenceData = mapToLpgContactPreferenceProps(account);

  return {
    [FieldType.NAME]: {
      data: `${contact.firstName || ""} ${contact.lastName}`.trim(),
      useUpdate: FIELD_UPDATE_HOOK_UNSUPPORTED,
      editable: false,
    },
    [FieldType.DOB]: {
      data: contact.dateOfBirth
        ? formatISO(parseISO(contact.dateOfBirth), {
            representation: "date",
          })
        : null,
      useUpdate: () => {
        const [update, { data, loading, error }] = useUpdateDob();
        return [
          (value: string) =>
            update({
              variables: {
                accountNumber: account.accountNumber,
                dateOfBirth: value,
              },
            }),
          {
            success:
              data === undefined
                ? undefined
                : !!data?.updateAccount.contact.dateOfBirth,
            error,
            loading,
          },
        ];
      },
      // DOB is only editable when not yet set. Once entered, it becomes immutable for data integrity (GROOT-267).
      editable: !isDobSet(contact.dateOfBirth),
    },
    [FieldType.EMAIL]: {
      data: billing.email,
      useUpdate: () => {
        const [update, { data, loading, error }] = useUpdateEmail();
        return [
          (value: string) =>
            update({
              variables: {
                accountNumber: account.accountNumber,
                email: value,
              },
            }),
          {
            success:
              data === undefined
                ? undefined
                : !!data?.updateAccount.billing.email,
            error,
            loading,
          },
        ];
      },
      editable: true,
    },
    [FieldType.MOBILE]: {
      data: contact.mobile,
      useUpdate: () => {
        const [update, { data, loading, error }] = useUpdateMobile();
        return [
          (value: string) =>
            update({
              variables: {
                accountNumber: account.accountNumber,
                mobile: transformPhoneFromInternational(value),
              },
            }),
          {
            success:
              data === undefined
                ? undefined
                : !!data?.updateAccount.contact.mobile,
            error,
            loading,
          },
        ];
      },
      editable: true,
    },
    [FieldType.LANDLINE]: FIELD_UNSUPPORTED,
    [FieldType.HOME_PHONE]: {
      data: contact.landline,
      useUpdate: () => {
        const [update, { data, loading, error }] = useUpdateHomePhone();
        return [
          (value: string) =>
            update({
              variables: {
                accountNumber: account.accountNumber,
                homephone: transformPhoneFromInternational(value),
              },
            }),
          {
            success:
              data === undefined
                ? undefined
                : !!data?.updateAccount.contact.landline,
            error,
            loading,
          },
        ];
      },
      editable: true,
    },
    [FieldType.BILLING_ADDRESS]: {
      data: [
        {
          id: account.accountNumber,
          address: billing.postalAddress!,
        },
      ],
      useUpdate: () => {
        const [update, { data, loading, error }] = useUpdatePostalAddress();
        return [
          (value: Address) => {
            update({
              variables: {
                accountNumber: account.accountNumber,
                postalAddress: mapAddressToAustralianAddressInput(value),
              },
            });
          },
          {
            success:
              data === undefined
                ? undefined
                : !!data?.updateAccount.billing.postalAddress,
            error,
            loading,
          },
        ];
      },
      editable: true,
    },
    [FieldType.CONTACT_PREFERENCE]: {
      data: contactPreferenceData ?? [],
      useUpdate: () => {
        const [update, { data, loading, error }] =
          useUpdateLpgAccountProfileContactPreference();
        return [
          (value: ContactPreferenceProp) => {
            if (!value.preference) {
              return;
            }
            return update({
              variables: {
                accountNumber: value.id!,
                preference: DeliveryType[value.preference],
              },
            });
          },
          {
            success:
              data === undefined
                ? undefined
                : !!data?.updateAccount.billing.type,
            data: mapBillingDetailsToContactPreferenceProp(
              data?.updateAccount?.billing
            ),
            error,
            loading,
          },
        ];
      },
      editable: true,
    },
    [FieldType.BUSINESS_ACCOUNT]: FIELD_UNSUPPORTED,
  };
};

export const useLpgProfileFieldMap = (): ProfileFieldMapResult => {
  const { data, refetch, error, loading } = useLpgAccountDetails();
  return {
    fieldMap: mapPlugin(data?.viewer),
    refetch,
    error,
    loading,
  };
};
